<template>
  <q-card class="partner-form q-pa-md q-mx-auto">
    <q-card-section class="text-center">
      <h4 class="text-h5 text-bold q-my-none">
        {{ content.join_us }}
      </h4>
    </q-card-section>
    <q-card-section>
      <q-input
        v-model="partner.title"
        class="q-mb-lg"
        :label="content.company_name"
      />
      <q-input
        v-model="partner.number"
        class="booking-plan q-mb-lg"
        :label="content.booking_plan"
        type="number"
      />
      <q-btn
        class="btn-shadow full-width q-mb-lg text-bold"
        color="positive"
        :label="content.join_now"
        @click="showModal"
      />
    </q-card-section>
    <q-dialog
      ref="modal"
      :maximized="$q.screen.lt.sm"
    >
      <q-card class="modal q-pa-lg">
        <q-card-section class="q-pa-none text-right">
          <q-btn
            v-close-popup
            class="text-grey"
            icon="close"
            dense
            flat
            round
          />
        </q-card-section>
        <q-card-section class="text-center">
          <h4 class="text-h5 text-bold q-my-none">{{ $t('button.sign_up') }}</h4>
        </q-card-section>
        <q-card-section>
          <q-form
            class="q-gutter-xs"
            @submit="onSubmit"
          >
            <div class="text-h6">{{ $t('label.company_info') }}</div>
            <q-input
              v-model="partner.title"
              :label="$t('placeholder.company_name')"
              :rules="[requiredField]"
              lazy-rules
            />
            <q-input
              v-model="partner.number"
              class="q-field--with-bottom"
              :label="content.booking_plan"
              type="number"
            />
            <q-input
              v-model="partner.registration_address"
              :label="$t('placeholder.company_address')"
              :rules="[requiredField]"
              lazy-rules
            />
            <q-select
              v-model="partner.country"
              :label="$t('placeholder.country')"
              :options="countriesOptions"
              :rules="[requiredField]"
              lazy-rules
              clearable
              clear-icon="close"
              option-label="title"
              option-value="iso_code"
              emit-value
              map-options
              use-input
              input-debounce="0"
              @filter="filterCountry"
            />
            <q-input
              v-model="partner.registration_number"
              :label="$t('placeholder.registration_number')"
              :rules="[requiredField]"
              lazy-rules
            />
            <q-select
              v-model="partner.ownership_type"
              :label="$t('placeholder.ownership_type')"
              :options="ownershipTypeOptions"
              :option-label="(item) => item && $t(`ownership_type.${item}`)"
              :rules="[requiredField]"
              lazy-rules
              clearable
              clear-icon="close"
              emit-value
              map-options
            />
            <q-select
              v-model="partner.type"
              :label="$t('placeholder.company_type')"
              :options="companyTypeOptions"
              :option-label="(item) => item && $t(`company_types.${item}`)"
              :rules="[requiredField]"
              lazy-rules
              clearable
              clear-icon="close"
              @input="partner.general_type_description = ''"
            />
            <q-slide-transition>
              <div v-if="partner.type === 'general'">
                <q-input
                  v-model="partner.general_type_description"
                  class="q-field--with-bottom"
                  :label="$t('placeholder.company_type_description')"
                />
              </div>
            </q-slide-transition>
            <div class="text-h6">{{ $t('label.business_contact') }}</div>
            <q-input
              v-model.trim="partner.account.full_name"
              :disable="account && !!account.full_name"
              :label="$t('placeholder.full_name')"
              :rules="[requiredField]"
              lazy-rules
            />
            <q-input
              v-model="partner.account.email"
              :disable="account && !!account.email"
              :label="$t('placeholder.email')"
              :rules="[requiredField]"
              lazy-rules
              type="email"
            />
            <intl-tel-input
              v-model="partner.account.phone"
              :disable="account && !!account.phone"
              :label="$t('placeholder.phone')"
              required
            />
            <q-field
              :value="partner.account.terms_accepted"
              :rules="[v => v || $t('error.terms_accepted')]"
              lazy-rules
              borderless
            >
              <template #control>
                <div class="fs-14 text-bold">
                  <div
                    v-html="$t('pages.for_business.accept_terms.service_agreement', {
                      service: $router.resolve({
                        name: 'terms_of_use',
                      }, $route).href,
                      partnership: $router.resolve({
                        name: 'partnership_agreement',
                      }, $route).href,
                      privacy: $router.resolve({
                        name: 'privacy_policy',
                      }, $route).href,
                    })"
                  />
                </div>
              </template>
              <template #prepend>
                <q-checkbox v-model="partner.account.terms_accepted" />
              </template>
            </q-field>
            <div class="q-mb-lg">
              <q-btn
                class="btn-shadow full-width text-bold"
                color="positive"
                type="submit"
                :label="$t('button.sign_up')"
              />
            </div>
            <div
              v-if="!account"
              class="text-center fs-14 q-mt-md text-bold"
            >
              {{ $t(`label.company_have_account`) }}
              <router-link :to="{ name: 'account.login' }">
                {{ $t('button.log_in') }}
              </router-link>
            </div>
            <div
              v-if="!carrier"
              class="text-center fs-14 q-mt-md text-bold"
            >
              <router-link :to="{ name: 'carrier.new' }">
                {{ $t('button.click_here') }}
              </router-link>
              {{ $t(`label.join_as_driver`) }}
            </div>
          </q-form>
        </q-card-section>
      </q-card>
    </q-dialog>
  </q-card>
</template>

<script>
import { mapGetters } from 'vuex';

import IntlTelInput from '~components/IntlTelInput';

import { RequiredField } from '~mixins';

export default {
  name: 'PartnerForm',
  components: {
    IntlTelInput,
  },
  mixins: [
    RequiredField,
  ],
  data() {
    return {
      countries: [],
      countriesOptions: [],
      ownershipTypeOptions: ['individual', 'organization'],
      companyTypeOptions: [
        'hotel',
        'airline',
        'booking_platform',
        'corporate',
        'sport',
        'leisure_and_entertainment',
        'loyalty',
        'mice',
        'ota',
        'general',
      ],
      partner: {
        number: '',
        ownership_type: '',
        registration_address: '',
        registration_number: '',
        title: '',
        type: '',
        general_type_description: '',
        company_type: '',
        country: '',
        account: {
          full_name: '',
          email: '',
          phone: '',
          terms_accepted: false,
        },
      },
    };
  },
  computed: {
    ...mapGetters(['account', 'carrier']),
    content() {
      return this.$t('pages.for_business');
    },
  },
  async mounted() {
    if (this.account) {
      this.partner.account.email = this.account.email;
      this.partner.account.phone = this.account.phone;
      this.partner.account.full_name = this.account.full_name;
    }
    this.countries = (await this.$axios('/api/configs/countries')).data.countries;
  },
  methods: {
    async onSubmit() {
      try {
        await this.$axios.post('/api/partners', {
          partner: this.partner,
        });

        window.location.href = `/${this.$i18n.locale}/transfers/new`;
      } catch (err) {
        const { type } = err.response.data.error;

        if (type === 'email_taken') {
          this.$router.push({
            name: 'account.login',
            query: {
              email: this.partner.account.email,
            },
          });
        } else if (type === 'phone_taken') {
          this.$router.push({
            name: 'account.login',
            query: {
              phone: this.partner.account.phone,
            },
          });
        } else {
          console.error(err);
        }
      }
    },
    filterCountry(val, update) {
      update(() => {
        this.countriesOptions = val ? this.countries.filter(
          (el) => [el.title, el.iso_code].some(
            (item) => item.toLowerCase().includes(val.toLowerCase()),
          ),
        ) : this.countries;
      });
    },
    showModal() {
      this.$refs.modal.show();
    },
  },
};
</script>

<style lang="scss" scoped>
.modal {
  max-width: 600px;
}

.partner-form {
  width: 355px;
  border-radius: 16px;
  .booking-plan::v-deep {
    &:not(.q-field--float) .q-field__label {
      top: 0;
    }
    .q-field__label {
      top: 12px;
      white-space: normal;
      transition: all .2s;
    }
  }

  @media screen and (max-width: $breakpoint-xs-max) {
    width: 100%;
  }
}

.btn-shadow {
  box-shadow: 0 2px 24px hsla(0, 0%, 45.5%, .5);
}
</style>
